import type { Response, ResponseItem, UploadReference } from '@app/src/types/resourceExplorer'

/**
 * Compares two responses to determine if they have the same data.
 * Uses responseHash if available (backend-computed), otherwise falls back to field comparison.
 */
export function areResponsesEqual(draft: Response, submitted: Response): boolean {
  if (draft.responseHash && submitted.responseHash) {
    return draft.responseHash === submitted.responseHash
  }

  if (draft.items.length !== submitted.items.length) return false

  const submittedMap = new Map(submitted.items.map(item => [item.requestItemId, item]))

  return draft.items.every(draftItem => {
    const submittedItem = submittedMap.get(draftItem.requestItemId)
    if (!submittedItem) return false
    return areResponseItemsEqual(draftItem, submittedItem)
  })
}

/**
 * Compares two response items to determine if they have the same data.
 * Matches backend comparison logic from VerificationCarryOver.cs:
 * - Compares: cannotAnswer, answerStatus, answer (trimmed), selectedOptions (sorted, case-insensitive), file metadata
 * - Ignores: comment, coordinates
 */
function areResponseItemsEqual(draft: ResponseItem, submitted: ResponseItem): boolean {
  if (draft.cannotAnswer !== submitted.cannotAnswer) return false

  if (draft.answerStatus !== submitted.answerStatus) return false

  const normalizeAnswer = (val: unknown) => String(val ?? '').trim()
  if (normalizeAnswer(draft.answer) !== normalizeAnswer(submitted.answer)) {
    return false
  }

  const normalizeOptions = (opts?: string[]) =>
    [...(opts ?? [])].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'accent' }))
  const draftOptions = normalizeOptions(draft.selectedOptions)
  const submittedOptions = normalizeOptions(submitted.selectedOptions)
  if (draftOptions.length !== submittedOptions.length) return false
  if (!draftOptions.every((opt, i) => opt.localeCompare(submittedOptions[i], 'en', { sensitivity: 'accent' }) === 0)) {
    return false
  }

  const draftFiles = draft.uploadReferences ?? []
  const submittedFiles = submitted.uploadReferences ?? []
  if (draftFiles.length !== submittedFiles.length) return false

  const sortFiles = (files: UploadReference[]) => [...files].sort((a, b) => a.id - b.id)
  const draftFilesSorted = sortFiles(draftFiles)
  const submittedFilesSorted = sortFiles(submittedFiles)

  if (!draftFilesSorted.every((file, i) => file.id === submittedFilesSorted[i].id)) {
    return false
  }

  return true
}
